import { CheckCircle2 } from "lucide-react";
import { motion } from "framer-motion";
import Reveal, { Eyebrow } from "@/components/Reveal";

const EASE = [0.16, 1, 0.3, 1];

const PILLARS = [
  {
    id: "cumplimiento",
    span: "lg:col-span-7",
    title: "Cumplimiento regulatorio y normativo",
    tag: "CNE & Gobierno",
    subtitle:
      "Adecuación informática para el Consejo Nacional Electoral y organismos de la administración pública.",
    features: [
      "Auditoría de código fuente y seguridad electoral",
      "Protocolos de encriptación y trazabilidad de datos",
      "Certificación de infraestructura gubernamental",
      "Planes de contingencia y continuidad operativa",
    ],
    image:
      "https://images.pexels.com/photos/7103147/pexels-photo-7103147.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Auditoría y revisión de documentos normativos",
  },
  {
    id: "infraestructura",
    span: "lg:col-span-5",
    title: "Infraestructura TI, redes y ciberseguridad",
    tag: "Misión crítica",
    subtitle: "Arquitectura física y lógica diseñada para resistir amenazas avanzadas y cero caídas.",
    features: [
      "Cableado estructurado y fibra óptica regional",
      "Firewalls Next-Gen y SOC",
      "Virtualización de servidores y cloud híbrido",
      "Protección DDoS y cifrado punto a punto",
    ],
    image:
      "https://images.pexels.com/photos/37730211/pexels-photo-37730211.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Centro de datos con servidores de alta disponibilidad",
  },
  {
    id: "software",
    span: "lg:col-span-12",
    title: "Desarrollo de software y transformación digital",
    tag: "A medida",
    subtitle:
      "Plataformas a medida con arquitectura limpia, alta concurrencia y despliegue continuo.",
    features: [
      "Sistemas de gestión institucional y gubernamental",
      "APIs seguras e integración de sistemas legados",
      "Aplicaciones web y móviles de alta demanda",
      "Automatización de procesos críticos",
    ],
    image:
      "https://images.pexels.com/photos/6804068/pexels-photo-6804068.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    alt: "Equipo de desarrollo de software trabajando",
  },
];

const Card = ({ p, i }) => (
  <motion.article
    initial={{ opacity: 0, y: 36 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, amount: 0.15 }}
    transition={{ duration: 0.85, ease: EASE, delay: i * 0.1 }}
    whileHover={{ scale: 1.012 }}
    data-testid={`service-card-${p.id}`}
    className={`group col-span-12 ${p.span} overflow-hidden rounded-[1.6rem] border border-slate-900/10 bg-white shadow-[0_1px_2px_rgba(15,23,42,0.05)] transition-shadow duration-500 hover:shadow-[0_24px_70px_-30px_rgba(0,240,255,0.35)]`}
  >
    <div className={`grid gap-0 ${p.id === "software" ? "lg:grid-cols-[1.1fr_1fr]" : "grid-cols-1"}`}>
      <div className={`flex flex-col p-7 sm:p-9 ${p.id === "software" ? "order-2 lg:order-1" : ""}`}>
        <div className="mb-5 flex items-center gap-3">
          <span className="rounded-full border border-cyan-600/25 bg-cyan-50 px-3.5 py-1.5 font-mono text-[10px] font-semibold uppercase tracking-[0.2em] text-cyan-700">
            {p.tag}
          </span>
        </div>
        <h3 className="font-heading text-2xl font-bold tracking-tight text-slate-900 sm:text-[1.7rem]">
          {p.title}
        </h3>
        <p className="mt-3 font-body text-[15px] leading-relaxed text-slate-600">{p.subtitle}</p>
        <ul className="mt-6 grid gap-3 sm:grid-cols-2">
          {p.features.map((f) => (
            <li key={f} className="flex items-start gap-2.5 font-body text-[13.5px] leading-snug text-slate-700">
              <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-cyan-600" />
              {f}
            </li>
          ))}
        </ul>
      </div>
      <div className={`relative min-h-[220px] overflow-hidden ${p.id === "software" ? "order-1 lg:order-2" : ""}`}>
        <img
          src={p.image}
          alt={p.alt}
          loading="lazy"
          className="absolute inset-0 h-full w-full object-cover transition-transform duration-[1.2s] ease-out group-hover:scale-[1.05]"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-white/40 via-transparent to-transparent" />
      </div>
    </div>
  </motion.article>
);

const Services = () => (
  <section id="servicios" className="scroll-mt-24 bg-paper py-24 sm:py-28" data-testid="services-section">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <div className="mb-14 flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">
        <Reveal>
          <Eyebrow dark>01 — Portafolio de servicios</Eyebrow>
          <h2 className="mt-4 max-w-2xl font-heading text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl lg:text-[2.9rem] lg:leading-[1.1]">
            Tres pilares. Una sola obsesión: que nada falle.
          </h2>
        </Reveal>
        <Reveal delay={0.15}>
          <p className="max-w-sm font-body text-[15px] leading-relaxed text-slate-600">
            Diseñamos, certificamos y operamos la tecnología con la que las instituciones más
            exigentes del país trabajan cada día.
          </p>
        </Reveal>
      </div>

      <div className="grid grid-cols-12 gap-5">
        {PILLARS.map((p, i) => (
          <Card key={p.id} p={p} i={i} />
        ))}
      </div>
    </div>
  </section>
);

export default Services;
